import { useState, useRef, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  useBrandingSettings,
  useUpdateBranding,
  useUploadLogo,
  useDeleteLogo,
  useDomainSettings,
  useUpdateDomain,
  useDeleteDomain,
  type UpdateBrandingPayload,
  type ThemePreset,
  type ColorMode,
  type DomainVerificationStatus,
} from './hooks/useBranding';

// ─── Constants ──────────────────────────────────────────────────────────────

const THEME_PRESETS: Array<{ value: ThemePreset; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'professional', label: 'Professional' },
  { value: 'modern', label: 'Modern' },
  { value: 'classic', label: 'Classic' },
  { value: 'minimal', label: 'Minimal' },
];

const COLOR_MODES: Array<{ value: ColorMode; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System Default' },
];

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_LOGO_TYPES = ['image/png', 'image/svg+xml'];

// ─── Form State ─────────────────────────────────────────────────────────────

interface BrandingFormState {
  firmName: string;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  themePreset: ThemePreset;
  colorMode: ColorMode;
}

const DEFAULT_FORM_STATE: BrandingFormState = {
  firmName: '',
  primaryColor: '#2563eb',
  secondaryColor: '#7c3aed',
  fontColor: '#111827',
  themePreset: 'default',
  colorMode: 'system',
};

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * BrandingPage — branding and white-labeling settings with live preview.
 * Requirements: 7.1, 7.3, 7.7, 7.9
 */
export function BrandingPage() {
  const { data: branding, isLoading: brandingLoading } = useBrandingSettings();
  const { data: domain, isLoading: domainLoading } = useDomainSettings();

  const updateBranding = useUpdateBranding();
  const uploadLogo = useUploadLogo();
  const deleteLogo = useDeleteLogo();
  const updateDomain = useUpdateDomain();
  const deleteDomain = useDeleteDomain();

  const [form, setForm] = useState<BrandingFormState>(DEFAULT_FORM_STATE);
  const [customDomain, setCustomDomain] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync form state with loaded data
  if (branding && !initialized) {
    setForm({
      firmName: branding.firmName,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      fontColor: branding.fontColor || '#111827',
      themePreset: branding.themePreset,
      colorMode: branding.colorMode,
    });
    setLogoPreview(branding.logoUrl);
    setInitialized(true);
  }

  if (domain && !customDomain && domain.customDomain) {
    setCustomDomain(domain.customDomain);
  }

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleFormChange = useCallback((field: keyof BrandingFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLogoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setLogoError(null);

      if (!file) return;

      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        setLogoError('Logo must be PNG or SVG format');
        return;
      }

      if (file.size > MAX_LOGO_SIZE) {
        setLogoError('Logo must be under 2 MB');
        return;
      }

      // Show local preview
      const objectUrl = URL.createObjectURL(file);
      setLogoPreview(objectUrl);

      // Upload
      uploadLogo.mutate(file, {
        onError: () => {
          setLogoError('Failed to upload logo');
          setLogoPreview(branding?.logoUrl || null);
        },
      });
    },
    [branding?.logoUrl, uploadLogo],
  );

  const handleLogoRemove = useCallback(() => {
    setLogoPreview(null);
    setLogoError(null);
    deleteLogo.mutate();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [deleteLogo]);

  const handleBrandingSave = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const payload: UpdateBrandingPayload = {
        firmName: form.firmName,
        primaryColor: form.primaryColor,
        secondaryColor: form.secondaryColor,
        fontColor: form.fontColor,
        themePreset: form.themePreset,
        colorMode: form.colorMode,
      };
      updateBranding.mutate(payload);
    },
    [form, updateBranding],
  );

  const handleDomainSave = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (customDomain.trim()) {
        updateDomain.mutate({ customDomain: customDomain.trim() });
      }
    },
    [customDomain, updateDomain],
  );

  const handleDomainRemove = useCallback(() => {
    setCustomDomain('');
    deleteDomain.mutate();
  }, [deleteDomain]);

  const handleReset = useCallback(() => {
    if (branding) {
      setForm({
        firmName: branding.firmName,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        fontColor: branding.fontColor || '#111827',
        themePreset: branding.themePreset,
        colorMode: branding.colorMode,
      });
      setLogoPreview(branding.logoUrl);
    }
  }, [branding]);

  // ─── Derived State ──────────────────────────────────────────────────────

  const hasChanges = useMemo(() => {
    if (!branding) return false;
    return (
      form.firmName !== branding.firmName ||
      form.primaryColor !== branding.primaryColor ||
      form.secondaryColor !== branding.secondaryColor ||
      form.fontColor !== (branding.fontColor || '#111827') ||
      form.themePreset !== branding.themePreset ||
      form.colorMode !== branding.colorMode
    );
  }, [form, branding]);

  // ─── Loading State ──────────────────────────────────────────────────────

  if (brandingLoading || domainLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12" role="status" aria-label="Loading">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left Column: Form */}
      <div className="space-y-6 lg:col-span-2">
        <form onSubmit={handleBrandingSave} className="space-y-6">
          {/* Firm Branding */}
          <Card>
            <CardHeader
              title="Branding"
              description="Customize your firm's appearance across the platform and client portal."
            />
            <CardContent>
              <div className="space-y-4">
                <Input
                  label="Firm Name"
                  value={form.firmName}
                  onChange={(e) => handleFormChange('firmName', e.target.value)}
                  required
                  placeholder="Your Firm Name"
                />

                {/* Logo Upload */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-border bg-white dark:border-border-dark dark:bg-surface-dark-secondary">
                        <img
                          src={logoPreview}
                          alt="Firm logo preview"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                        <svg
                          className="h-6 w-6 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                          />
                        </svg>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          loading={uploadLogo.isPending}
                        >
                          Upload Logo
                        </Button>
                        {logoPreview && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleLogoRemove}
                            loading={deleteLogo.isPending}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG or SVG, max 2 MB
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.svg,image/png,image/svg+xml"
                      onChange={handleLogoSelect}
                      className="hidden"
                      aria-label="Upload logo file"
                    />
                  </div>
                  {logoError && (
                    <p className="mt-1.5 text-sm text-danger-500" role="alert">
                      {logoError}
                    </p>
                  )}
                </div>

                {/* Colors */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="primary-color"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Primary Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        id="primary-color"
                        type="color"
                        value={form.primaryColor}
                        onChange={(e) => handleFormChange('primaryColor', e.target.value)}
                        className="h-10 w-10 cursor-pointer rounded-lg border border-border dark:border-border-dark"
                        aria-label="Primary color picker"
                      />
                      <Input
                        value={form.primaryColor}
                        onChange={(e) => handleFormChange('primaryColor', e.target.value)}
                        placeholder="#2563eb"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="secondary-color"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Secondary Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        id="secondary-color"
                        type="color"
                        value={form.secondaryColor}
                        onChange={(e) => handleFormChange('secondaryColor', e.target.value)}
                        className="h-10 w-10 cursor-pointer rounded-lg border border-border dark:border-border-dark"
                        aria-label="Secondary color picker"
                      />
                      <Input
                        value={form.secondaryColor}
                        onChange={(e) => handleFormChange('secondaryColor', e.target.value)}
                        placeholder="#7c3aed"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="font-color"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Font Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        id="font-color"
                        type="color"
                        value={form.fontColor}
                        onChange={(e) => handleFormChange('fontColor', e.target.value)}
                        className="h-10 w-10 cursor-pointer rounded-lg border border-border dark:border-border-dark"
                        aria-label="Font color picker"
                      />
                      <Input
                        value={form.fontColor}
                        onChange={(e) => handleFormChange('fontColor', e.target.value)}
                        placeholder="#111827"
                        className="font-mono"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Default text color for the back office.
                    </p>
                  </div>
                </div>

                {/* Theme Preset */}
                <Select
                  label="Theme Preset"
                  value={form.themePreset}
                  onChange={(e) => handleFormChange('themePreset', e.target.value)}
                  options={THEME_PRESETS}
                />

                {/* Color Mode */}
                <Select
                  label="Color Mode"
                  value={form.colorMode}
                  onChange={(e) => handleFormChange('colorMode', e.target.value)}
                  options={COLOR_MODES}
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button type="button" variant="outline" onClick={handleReset} disabled={!hasChanges}>
                Reset
              </Button>
              <Button type="submit" loading={updateBranding.isPending} disabled={!hasChanges}>
                Save Branding
              </Button>
            </CardFooter>
          </Card>
        </form>

        {/* Custom Domain */}
        <Card>
          <CardHeader
            title="Custom Domain"
            description="Map a custom domain to your client portal for white-labeled access."
          />
          <CardContent>
            <form onSubmit={handleDomainSave} className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Input
                    label="Domain"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    placeholder="portal.yourfirm.ca"
                    hint="Point a CNAME record to portal.taxplatform.io"
                  />
                </div>
                <Button
                  type="submit"
                  size="md"
                  loading={updateDomain.isPending}
                  disabled={!customDomain.trim()}
                >
                  Save
                </Button>
              </div>

              {domain && domain.verificationStatus !== 'not_configured' && (
                <div className="rounded-lg border border-border p-4 dark:border-border-dark">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {domain.customDomain}
                      </p>
                      <div className="flex items-center gap-2">
                        <DomainStatusBadge status={domain.verificationStatus} />
                        {domain.sslStatus === 'active' && (
                          <Badge variant="success" size="sm">
                            SSL Active
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDomainRemove}
                      loading={deleteDomain.isPending}
                    >
                      Remove
                    </Button>
                  </div>

                  {domain.verificationToken && domain.verificationStatus === 'pending' && (
                    <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Add this TXT record to verify ownership:
                      </p>
                      <code className="mt-1 block break-all text-xs text-gray-900 dark:text-gray-100">
                        {domain.verificationToken}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Right Column: Live Preview */}
      <div className="lg:col-span-1">
        <div className="sticky top-6">
          <BrandingPreview
            firmName={form.firmName}
            logoUrl={logoPreview}
            primaryColor={form.primaryColor}
            secondaryColor={form.secondaryColor}
            fontColor={form.fontColor}
            themePreset={form.themePreset}
            colorMode={form.colorMode}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function DomainStatusBadge({ status }: { status: DomainVerificationStatus }) {
  switch (status) {
    case 'verified':
      return (
        <Badge variant="success" size="sm" dot>
          Verified
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="warning" size="sm" dot>
          Pending Verification
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="danger" size="sm" dot>
          Verification Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="default" size="sm">
          Not Configured
        </Badge>
      );
  }
}

interface BrandingPreviewProps {
  firmName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  themePreset: ThemePreset;
  colorMode: ColorMode;
}

function BrandingPreview({
  firmName,
  logoUrl,
  primaryColor,
  secondaryColor,
  fontColor,
  themePreset,
  colorMode,
}: BrandingPreviewProps) {
  const isDark = colorMode === 'dark';
  const bgColor = isDark ? '#1f2937' : '#ffffff';
  // In light mode the branded font color drives body text; dark mode keeps a
  // readable light color regardless.
  const textColor = isDark ? '#f9fafb' : fontColor;
  const mutedColor = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? '#374151' : '#e5e7eb';

  const presetStyles = useMemo(() => {
    switch (themePreset) {
      case 'professional':
        return { borderRadius: '4px', fontWeight: '600' };
      case 'modern':
        return { borderRadius: '12px', fontWeight: '500' };
      case 'classic':
        return { borderRadius: '2px', fontWeight: '700' };
      case 'minimal':
        return { borderRadius: '0px', fontWeight: '400' };
      default:
        return { borderRadius: '8px', fontWeight: '500' };
    }
  }, [themePreset]);

  return (
    <Card>
      <CardHeader title="Live Preview" description="See how your branding will appear." />
      <CardContent>
        <div
          className="overflow-hidden rounded-lg border"
          style={{ borderColor }}
          role="region"
          aria-label="Branding preview"
        >
          {/* Preview Header */}
          <div
            className="flex items-center gap-3 px-4 py-3"
            style={{ backgroundColor: primaryColor }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo preview"
                className="h-8 w-8 rounded object-contain"
                style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
              />
            ) : (
              <div
                className="flex h-8 w-8 items-center justify-center rounded text-xs font-bold text-white"
                style={{ backgroundColor: secondaryColor }}
              >
                {firmName?.charAt(0)?.toUpperCase() || 'F'}
              </div>
            )}
            <span
              className="text-sm font-semibold text-white"
              style={{ fontWeight: presetStyles.fontWeight }}
            >
              {firmName || 'Your Firm'}
            </span>
          </div>

          {/* Preview Body */}
          <div className="space-y-3 p-4" style={{ backgroundColor: bgColor }}>
            {/* Nav Items */}
            <div className="flex gap-2">
              <div
                className="px-3 py-1 text-xs text-white"
                style={{
                  backgroundColor: primaryColor,
                  borderRadius: presetStyles.borderRadius,
                }}
              >
                Dashboard
              </div>
              <div
                className="px-3 py-1 text-xs"
                style={{
                  color: mutedColor,
                  border: `1px solid ${borderColor}`,
                  borderRadius: presetStyles.borderRadius,
                }}
              >
                Documents
              </div>
              <div
                className="px-3 py-1 text-xs"
                style={{
                  color: mutedColor,
                  border: `1px solid ${borderColor}`,
                  borderRadius: presetStyles.borderRadius,
                }}
              >
                Invoices
              </div>
            </div>

            {/* Content Preview */}
            <div
              className="space-y-2 p-3"
              style={{
                border: `1px solid ${borderColor}`,
                borderRadius: presetStyles.borderRadius,
              }}
            >
              <p className="text-xs font-medium" style={{ color: textColor }}>
                Sample heading text
              </p>
              <div className="h-2 w-3/4 rounded" style={{ backgroundColor: borderColor }} />
              <div className="h-2 w-1/2 rounded" style={{ backgroundColor: borderColor }} />
              <div className="mt-3 flex gap-2">
                <div
                  className="px-3 py-1 text-xs text-white"
                  style={{
                    backgroundColor: primaryColor,
                    borderRadius: presetStyles.borderRadius,
                  }}
                >
                  Action
                </div>
                <div
                  className="px-3 py-1 text-xs"
                  style={{
                    color: secondaryColor,
                    border: `1px solid ${secondaryColor}`,
                    borderRadius: presetStyles.borderRadius,
                  }}
                >
                  Secondary
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-2xs" style={{ color: mutedColor }}>
              Powered by {firmName || 'Your Firm'}
            </p>
          </div>
        </div>

        {/* Preview Info */}
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: primaryColor }}
            />
            Primary: {primaryColor}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: secondaryColor }}
            />
            Secondary: {secondaryColor}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Theme: {themePreset} · Mode: {colorMode}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
